/**
 * Fetches all the records based on query and sorts them with there corresponding relations
 * UserAssessments, answersByItem, byItem, byNode, bySubtest, byTest
 * @param {String or Bolean} Id or Bolean
 * @param {Object} takes a students object
 * @param {Object} items
 * @param {String} assessment sets for destructured subtest based on assessmentId
 * @param {Number} assessmentId
 * @returns {object}
 */

import { db } from "../../../mp-database"
import forOwn from "mout/object/forOwn"
import initializeAnswers from "./initializeAnswers"
import parseSnapshot from "../parseSnapshot"
import { chunk } from "lodash"

export default async function getStudentsScoring(
  students,
  items,
  assessmentSets,
  assessmentId,
  year,
  limit,
  grabAll
) {
  return Promise.all([
    getAnswersByItem(students, assessmentId, year, limit, grabAll),
    getScoringByTest(students, assessmentId, year, limit, grabAll),
    getScoringBySubtest(students, assessmentId, year, limit, grabAll),
    getScoringByItem(students, assessmentId, year, limit, grabAll),
    getScoringByNode(students, assessmentId, year, limit, grabAll)
  ])
    .then(results => {
      const [aByItem, sByTest, sBySubtest, sByItem, sByNode] = results
      const subtestIds = Object.keys(
        assessmentSets[assessmentId].assessmentSubtests
      )
      const answers = initializeAnswers(
        items,
        assessmentSets[assessmentId].destructuredSubtests
      )
      let studentScoring = {}
      let studentsObj = Object.assign(
        {},
        ...students.map(student => ({ [student.id]: student }))
      )

      forOwn(sByTest, (uA, id) => {
        const { firstName, lastName, fullName, email, username, school } = uA
        studentScoring[`${uA.userId}_${uA.userAssessmentId}`] = {
          answers,
          answersByItem: sortResults(aByItem, id, "answers", subtestIds),
          scoring: {
            byTest: sByTest[id] ? sByTest[id].byTest : {},
            bySubtest: sortResults(sBySubtest, id, "bySubtest", subtestIds),
            byItem: sortResults(sByItem, id, "byItem", subtestIds),
            byNode: sortResults(sByNode, id, "byNode", subtestIds)
          },
          userAssessmentId: uA.userAssessmentId,
          userId: uA.userId,
          assessmentSetId: uA.assessmentSetId,
          firstName: firstName || "",
          lastName: lastName || "",
          fullName: fullName || "",
          email: email || "",
          username: username || "",
          school: school || ""
        }
      })
      return studentScoring
    })
    .catch(err => {
      console.log(err)
      throw err
    })
}

async function getAnswersByItem(students, assessmentId, year) {
  const chunks = chunk(students, 10)
  const promises = []
  let scores = {}

  chunks.forEach(c => {
    promises.push(
      db
        .collection("answersByItem")
        .where("assessmentId", "==", assessmentId)
        .where("year", "==", Number(year))
        .where(
          "studentId",
          "in",
          c.map(s => s.id)
        )
        .get()
    )
  })

  await Promise.all(promises).then(promise => {
    promise.forEach(docs => {
      scores = {
        ...scores,
        ...parseSnapshot(docs)
      }
    })
  })

  return scores
}

async function getScoringByTest(students, assessmentId, year) {
  const chunks = chunk(students, 10)
  const promises = []
  let scores = {}

  chunks.forEach(c => {
    promises.push(
      db
        .collection("scoringByTest")
        .where("assessmentId", "==", assessmentId)
        .where("year", "==", Number(year))
        .where(
          "userId",
          "in",
          c.map(s => s.id)
        )
        .get()
    )
  })

  await Promise.all(promises).then(promise => {
    promise.forEach(docs => {
      scores = {
        ...scores,
        ...parseSnapshot(docs)
      }
    })
  })

  return scores
}

async function getScoringBySubtest(students, assessmentId, year) {
  const chunks = chunk(students, 10)
  const promises = []
  let scores = {}

  chunks.forEach(c => {
    promises.push(
      db
        .collection("scoringBySubtest")
        .where("assessmentId", "==", assessmentId)
        .where("year", "==", Number(year))
        .where(
          "userId",
          "in",
          c.map(s => s.id)
        )
        .get()
    )
  })

  await Promise.all(promises).then(promise => {
    promise.forEach(docs => {
      scores = {
        ...scores,
        ...parseSnapshot(docs)
      }
    })
  })

  return scores
}

async function getScoringByItem(students, assessmentId, year) {
  const chunks = chunk(students, 10)
  const promises = []
  let scores = {}

  chunks.forEach(c => {
    promises.push(
      db
        .collection("scoringByItem")
        .where("assessmentId", "==", assessmentId)
        .where("year", "==", Number(year))
        .where(
          "userId",
          "in",
          c.map(s => s.id)
        )
        .get()
    )
  })

  await Promise.all(promises).then(promise => {
    promise.forEach(docs => {
      scores = {
        ...scores,
        ...parseSnapshot(docs)
      }
    })
  })

  return scores
}

async function getScoringByNode(students, assessmentId, year) {
  const chunks = chunk(students, 10)
  const promises = []
  let scores = {}

  chunks.forEach(c => {
    promises.push(
      db
        .collection("scoringByNode")
        .where("assessmentId", "==", assessmentId)
        .where("year", "==", Number(year))
        .where(
          "userId",
          "in",
          c.map(s => s.id)
        )
        .get()
    )
  })

  await Promise.all(promises).then(promise => {
    promise.forEach(docs => {
      scores = {
        ...scores,
        ...parseSnapshot(docs)
      }
    })
  })

  return scores
}

function sortResults(results, userAssessmentId, key, subtestIds) {
  let scoreObj = {}

  subtestIds.forEach(subtestId => {
    const docId = `${userAssessmentId}_${subtestId}`
    if (results[docId]) {
      scoreObj[subtestId] = results[docId][key]

      if (key === "answers" && results[docId].gridInputs) {
        scoreObj[subtestId] = {
          ...scoreObj[subtestId],
          gridInputs: results[docId].gridInputs
        }
      }
    }
  })
  return scoreObj
}
